import {useEffect, useMemo, useState} from 'react';

import {Box, ButtonBase, Dialog, DialogContent, InputBase, Stack, Typography} from '@mui/material';

import VarInput from '../../contentGenerator/components/VarInput';
import {extractFormulaVarNames} from '../../contentGenerator/formulaUtils';
import {Spinner} from '../../contentGenerator/components/ui';
import {
    C,
    CMD_COLORS,
    TYPE_CONFIG,
} from '../constants';
import {useReviewAI} from '../hooks/useReviewAI';
import {importFile} from '../../shared/exportService';
import {
    createField,
    normalizeListing,
    normalizeListings,
    parseFieldSubgroupKey,
    uid,
} from '../utils';
import {SectionLabel} from './shared';

const dialogPaperSx = {
    background: C.surface,
    borderRadius: '16px',
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
};

export function EmptyDetail() {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 320,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: C.textSoft,
                padding: '2rem',
            }}>
            Select a listing from the sidebar, or load new listings to start reviewing.
        </Box>
    );
}

export function AddFieldPanel({onAdd, onClose}) {
    const [type, setType] = useState('bullet');
    const [label, setLabel] = useState('');
    const [text, setText] = useState('');

    return (
        <Box
            sx={{
                border: `1px solid ${C.purple}33`,
                background: C.purpleL,
                borderRadius: '12px',
                padding: '14px',
                marginBottom: '12px',
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{marginBottom: '10px'}}>
                <SectionLabel sx={{color: C.purple}}>Add field</SectionLabel>
                <ButtonBase
                    onClick={onClose}
                    sx={{border: 'none', background: 'transparent', color: C.textSoft, cursor: 'pointer', fontSize: 18, lineHeight: 1}}>
                    ×
                </ButtonBase>
            </Stack>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px'}}>
                <Box>
                    <SectionLabel sx={{marginBottom: '4px'}}>Type</SectionLabel>
                    <Box
                        component="select"
                        value={type}
                        onChange={(event) => setType(event.target.value)}
                        sx={{
                            width: '100%',
                            fontFamily: 'inherit',
                            fontSize: 12,
                            padding: '8px 10px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border2}`,
                            background: C.surface,
                        }}>
                        {Object.keys(TYPE_CONFIG).map((item) => (
                            <option key={item} value={item}>
                                {TYPE_CONFIG[item].label}
                            </option>
                        ))}
                    </Box>
                </Box>
                <Box>
                    <SectionLabel sx={{marginBottom: '4px'}}>Label</SectionLabel>
                    <InputBase
                        value={label}
                        onChange={(event) => setLabel(event.target.value)}
                        placeholder={TYPE_CONFIG[type].label}
                        sx={{
                            width: '100%',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            fontSize: 12,
                            padding: '8px 10px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border2}`,
                            background: C.surface,
                        }}
                    />
                </Box>
            </Box>
            <Box sx={{marginTop: '10px'}}>
                <SectionLabel sx={{marginBottom: '4px'}}>Content</SectionLabel>
                <InputBase
                    multiline
                    rows={4}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    placeholder="Paste or type field content"
                    sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: `1px solid ${C.border2}`,
                        background: C.surface,
                        '& textarea': {resize: 'vertical'},
                    }}
                />
            </Box>
            <ButtonBase
                onClick={() => {
                    if (!text.trim()) return;
                    onAdd(
                        createField({
                            id: `f${uid()}`,
                            type,
                            label: label.trim() || TYPE_CONFIG[type].label,
                            text: text.trim(),
                        }),
                    );
                    onClose();
                }}
                sx={{
                    marginTop: '10px',
                    width: '100%',
                    border: 'none',
                    background: text.trim() ? C.purple : C.surfaceAlt,
                    color: text.trim() ? C.surface : C.textSoft,
                    borderRadius: '10px',
                    padding: '9px 12px',
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    cursor: 'pointer',
                }}>
                Add field
            </ButtonBase>
        </Box>
    );
}

function rowToListing(row) {
    // CSV columns store subgroup metadata as pipe-separated strings like
    // "barcode:810165452466|custom_finish:Brushed Nickel|orientation:Left-Hand".
    // createField expects an OBJECT shape ({uniqueVarNames, groupKey,
    // valuesByName}), so we parse the string before passing it through.
    // Without this, all imported fields end up with subgroupMeta=null and
    // the variant tabs in ListingDetail can't merge listings into a group.
    const fields = [];
    if (row.title) {
        fields.push(createField({
            type: 'title',
            label: 'Title',
            text: row.title,
            subgroupMeta: parseFieldSubgroupKey(row.title_subgroup || ''),
        }));
    }
    if (row.description) {
        fields.push(createField({
            type: 'description',
            label: 'Description',
            text: row.description,
            subgroupMeta: parseFieldSubgroupKey(row.description_subgroup || ''),
        }));
    }
    for (let i = 1; i <= 20; i++) {
        const text = row[`bullet_${i}`];
        if (!text) continue;
        fields.push(createField({
            type: 'bullet',
            label: row[`bullet_${i}_label`] || `Bullet ${i}`,
            text,
            subgroupMeta: parseFieldSubgroupKey(row[`bullet_${i}_subgroup`] || ''),
        }));
    }
    // FAQ columns: faq_<n>_question, faq_<n>_answer (+ optional
    // _subgroup). Mirrors what formatReviewCsv / exportReviewListings
    // emit. A row with either a question or an answer becomes an FAQ
    // field; empty pairs are skipped.
    for (let i = 1; i <= 20; i++) {
        const question = (row[`faq_${i}_question`] || '').toString();
        const answer = (row[`faq_${i}_answer`] || '').toString();
        if (!question.trim() && !answer.trim()) continue;
        fields.push(createField({
            type: 'faq',
            label: `FAQ ${i}`,
            question,
            text: answer,
            subgroupMeta: parseFieldSubgroupKey(row[`faq_${i}_subgroup`] || ''),
        }));
    }
    return {
        name: row.name || '',
        sku: row.sku || '',
        superSku: row.group || row.super_sku || '',
        category: row.category || '',
        fields,
    };
}

export function ImportPanel({onImport}) {
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const [fileName, setFileName] = useState('');
    const [singleName, setSingleName] = useState('');
    const [singleSku, setSingleSku] = useState('');
    const [singleCategory, setSingleCategory] = useState('');
    const [singleTitle, setSingleTitle] = useState('');
    const [singleDescription, setSingleDescription] = useState('');

    const handleFileImport = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setError('');
        setImporting(true);
        try {
            const rows = await importFile(file);
            if (!rows.length) throw new Error('File is empty or has no data rows');
            const listings = normalizeListings(rows.map(rowToListing));
            onImport(listings);
        } catch (err) {
            setError(err.message || 'Failed to import file');
        } finally {
            setImporting(false);
            event.target.value = '';
        }
    };

    return (
        <Stack direction="column" gap="14px" sx={{maxWidth: 880}}>
            <Box
                sx={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: '14px',
                    padding: '16px',
                }}>
                <SectionLabel sx={{marginBottom: '6px'}}>Import from file</SectionLabel>
                <Typography sx={{fontSize: 12, color: C.textSoft, lineHeight: 1.6, marginBottom: '10px'}}>
                    Upload a CSV or XLSX file. Expected columns: name, sku, group, category,
                    title, description, bullet_1_label, bullet_1, bullet_2_label, bullet_2…,
                    faq_1_question, faq_1_answer, faq_2_question, faq_2_answer…
                </Typography>
                <Stack direction="row" alignItems="center" gap="10px">
                    <ButtonBase
                        component="label"
                        disabled={importing}
                        sx={{
                            border: 'none',
                            background: C.ink,
                            color: C.surface,
                            borderRadius: '10px',
                            padding: '9px 14px',
                            fontFamily: 'inherit',
                            fontWeight: 700,
                            cursor: importing ? 'not-allowed' : 'pointer',
                            opacity: importing ? 0.6 : 1,
                        }}>
                        {importing ? 'Importing...' : 'Choose file'}
                        <input
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileImport}
                            hidden
                        />
                    </ButtonBase>
                    {fileName && (
                        <Typography sx={{fontSize: 12, color: C.textSoft}}>{fileName}</Typography>
                    )}
                </Stack>
                {error && (
                    <Typography sx={{fontSize: 12, color: C.coral, marginTop: '8px'}}>{error}</Typography>
                )}
            </Box>

            <Box
                sx={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: '14px',
                    padding: '16px',
                }}>
                <SectionLabel sx={{marginBottom: '10px'}}>Quick add</SectionLabel>
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px'}}>
                    <InputBase
                        value={singleName}
                        onChange={(event) => setSingleName(event.target.value)}
                        placeholder="Listing name"
                        sx={{fontFamily: 'inherit', fontSize: 12, padding: '8px 10px', borderRadius: '10px', border: `1px solid ${C.border2}`}}
                    />
                    <InputBase
                        value={singleSku}
                        onChange={(event) => setSingleSku(event.target.value)}
                        placeholder="SKU"
                        sx={{fontFamily: 'inherit', fontSize: 12, padding: '8px 10px', borderRadius: '10px', border: `1px solid ${C.border2}`}}
                    />
                    <InputBase
                        value={singleCategory}
                        onChange={(event) => setSingleCategory(event.target.value)}
                        placeholder="Category"
                        sx={{fontFamily: 'inherit', fontSize: 12, padding: '8px 10px', borderRadius: '10px', border: `1px solid ${C.border2}`}}
                    />
                </Box>
                <Box sx={{display: 'grid', gap: '10px', marginTop: '10px'}}>
                    <InputBase
                        multiline
                        rows={2}
                        value={singleTitle}
                        onChange={(event) => setSingleTitle(event.target.value)}
                        placeholder="Title"
                        sx={{
                            fontFamily: 'inherit',
                            fontSize: 13,
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border2}`,
                            '& textarea': {resize: 'vertical'},
                        }}
                    />
                    <InputBase
                        multiline
                        rows={4}
                        value={singleDescription}
                        onChange={(event) => setSingleDescription(event.target.value)}
                        placeholder="Description"
                        sx={{
                            fontFamily: 'inherit',
                            fontSize: 13,
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border2}`,
                            '& textarea': {resize: 'vertical'},
                        }}
                    />
                </Box>
                <ButtonBase
                    onClick={() => {
                        if (!singleName.trim()) return;
                        const fields = [];
                        if (singleTitle.trim()) {
                            fields.push(createField({type: 'title', label: 'Title', text: singleTitle.trim()}));
                        }
                        if (singleDescription.trim()) {
                            fields.push(
                                createField({type: 'description', label: 'Description', text: singleDescription.trim()}),
                            );
                        }
                        onImport([
                            normalizeListing({
                                name: singleName.trim(),
                                sku: singleSku.trim(),
                                category: singleCategory.trim(),
                                fields,
                            }),
                        ]);
                        setSingleName('');
                        setSingleSku('');
                        setSingleCategory('');
                        setSingleTitle('');
                        setSingleDescription('');
                    }}
                    sx={{
                        marginTop: '10px',
                        border: 'none',
                        background: C.teal,
                        color: C.surface,
                        borderRadius: '10px',
                        padding: '9px 14px',
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}>
                    Add listing
                </ButtonBase>
            </Box>
        </Stack>
    );
}

export function ContentLimitsPanel({limits, profile}) {
    const [open, setOpen] = useState(false);
    const activeCount = Object.keys(TYPE_CONFIG).filter((type) => limits[type] > 0).length;
    const profileColor = profile?.color || C.teal;

    return (
        <Box sx={{borderTop: `1px solid ${C.border}`}}>
            <Stack
                component={ButtonBase}
                direction="row"
                alignItems="center"
                gap="8px"
                onClick={() => setOpen((current) => !current)}
                sx={{
                    width: '100%',
                    border: 'none',
                    background: 'transparent',
                    padding: '10px 12px',
                    color: C.ink,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                }}>
                <Typography component="span" sx={{flex: 1, fontSize: 12, fontWeight: 600, textAlign: 'left'}}>
                    Profile limits
                </Typography>
                {activeCount > 0 && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: profileColor,
                            background: `${profileColor}15`,
                            border: `1px solid ${profileColor}33`,
                            borderRadius: 999,
                            padding: '1px 7px',
                        }}>
                        {activeCount} active
                    </Typography>
                )}
                <Typography component="span" sx={{fontSize: 10, color: C.textSoft, transition: 'transform .15s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'block'}}>▾</Typography>
            </Stack>
            {open && (
                <Box sx={{padding: '0 12px 12px'}}>
                    <Typography sx={{fontSize: 10, color: C.textSoft, marginBottom: '8px', lineHeight: 1.5}}>
                        Review limits follow the active generator profile. Change them in profile settings.
                    </Typography>
                    {profile?.name && (
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: profileColor,
                                marginBottom: '8px',
                            }}>
                            Active profile: {profile.name}
                        </Typography>
                    )}
                    <Box sx={{display: 'grid', gap: '6px'}}>
                        {Object.keys(TYPE_CONFIG).map((type) => (
                            <Box
                                key={type}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                <Box
                                    component="span"
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        background: TYPE_CONFIG[type].color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography component="span" sx={{fontSize: 11, color: C.ink, flex: 1}}>
                                    {TYPE_CONFIG[type].label}
                                </Typography>
                                <Box
                                    sx={{
                                        minWidth: 92,
                                        textAlign: 'right',
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                        padding: '4px 7px',
                                        borderRadius: '7px',
                                        border: `1px solid ${limits[type] > 0 ? `${C.teal}55` : C.border2}`,
                                        background: limits[type] > 0 ? `${C.teal}15` : C.surfaceAlt,
                                        color: limits[type] > 0 ? C.teal : C.textSoft,
                                    }}
                                >
                                    {limits[type] > 0 ? `${limits[type]} chars` : 'No limit'}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export function CustomCommandsPanel({commands, setCommands, vars = []}) {
    const [open, setOpen] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState('');
    const [label, setLabel] = useState('');
    const [prompt, setPrompt] = useState('');
    const [colorId, setColorId] = useState(CMD_COLORS[0].id);
    const promptAttributeNames = useMemo(
        () => extractFormulaVarNames(prompt),
        [prompt],
    );
    const availableVarNames = useMemo(
        () => new Set(vars.map((variable) => variable.name)),
        [vars],
    );
    const unavailableAttributeNames = promptAttributeNames.filter(
        (name) => !availableVarNames.has(name),
    );

    const resetForm = () => {
        setLabel('');
        setPrompt('');
        setColorId(CMD_COLORS[0].id);
        setEditingId('');
        setShowForm(false);
    };

    const editCommand = (command) => {
        setEditingId(command.id);
        setLabel(command.label);
        setPrompt(command.prompt);
        setColorId(command.colorId || CMD_COLORS[0].id);
        setShowForm(true);
    };

    const addCommand = () => {
        setEditingId('');
        setLabel('');
        setPrompt('');
        setColorId(CMD_COLORS[0].id);
        setShowForm(true);
    };

    return (
        <Box sx={{borderTop: `1px solid ${C.border}`}}>
            <Stack
                component={ButtonBase}
                direction="row"
                alignItems="center"
                gap="8px"
                onClick={() => setOpen((current) => !current)}
                sx={{
                    width: '100%',
                    border: 'none',
                    background: 'transparent',
                    padding: '10px 12px',
                    color: C.ink,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                }}>
                <Typography component="span" sx={{flex: 1, fontSize: 12, fontWeight: 600, textAlign: 'left'}}>
                    Custom commands
                </Typography>
                {commands.length > 0 && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.purple,
                            background: C.purpleL,
                            border: `1px solid ${C.purple}33`,
                            borderRadius: 999,
                            padding: '1px 7px',
                        }}>
                        {commands.length}
                    </Typography>
                )}
                <Typography component="span" sx={{fontSize: 10, color: C.textSoft, transition: 'transform .15s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'block'}}>▾</Typography>
            </Stack>
            {open && (
                <Stack direction="column" gap="4px" sx={{padding: '0 12px 12px'}}>
                    {commands.map((command) => {
                        const color = CMD_COLORS.find((item) => item.id === command.colorId) || CMD_COLORS[0];
                        return (
                            <Stack
                                key={command.id}
                                direction="row"
                                alignItems="center"
                                gap="8px"
                                sx={{
                                    border: `1px solid ${
                                        editingId === command.id
                                            ? `${C.purple}44`
                                            : C.border
                                    }`,
                                    borderRadius: '8px',
                                    padding: '6px 8px',
                                    background:
                                        editingId === command.id ? C.purpleL : C.surface,
                                }}>
                                <Box
                                    component="span"
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        background: color.hex,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography sx={{fontSize: 12, fontWeight: 600, color: C.ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {command.label}
                                </Typography>
                                <ButtonBase
                                    onClick={() => editCommand(command)}
                                    aria-label={`Edit ${command.label}`}
                                    sx={{
                                        border: `1px solid ${C.purple}33`,
                                        background: editingId === command.id
                                            ? C.purple
                                            : 'transparent',
                                        color: editingId === command.id
                                            ? C.surface
                                            : C.purple,
                                        borderRadius: '6px',
                                        padding: '2px 7px',
                                        fontFamily: 'inherit',
                                        fontSize: 10,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                    }}>
                                    Edit
                                </ButtonBase>
                                <ButtonBase
                                    onClick={() => {
                                        setCommands((current) =>
                                            current.filter((item) => item.id !== command.id),
                                        );
                                        if (editingId === command.id) resetForm();
                                    }}
                                    aria-label={`Remove ${command.label}`}
                                    sx={{
                                        border: `1px solid ${C.border}`,
                                        background: 'transparent',
                                        color: C.textSoft,
                                        borderRadius: '6px',
                                        padding: '2px 6px',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}>
                                    ×
                                </ButtonBase>
                            </Stack>
                        );
                    })}

                    {!showForm ? (
                        <ButtonBase
                            onClick={addCommand}
                            sx={{
                                marginTop: commands.length ? '2px' : 0,
                                border: `1px dashed ${C.purple}44`,
                                background: 'transparent',
                                color: C.purple,
                                borderRadius: '8px',
                                padding: '6px 10px',
                                fontFamily: 'inherit',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'block',
                                width: '100%',
                                textAlign: 'center',
                            }}>
                            + Add command
                        </ButtonBase>
                    ) : (
                        <Box
                            sx={{
                                marginTop: '2px',
                                border: `1px solid ${C.purple}33`,
                                background: C.purpleL,
                                borderRadius: '10px',
                                padding: '10px',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: C.purple,
                                    marginBottom: '7px',
                                }}>
                                {editingId ? 'Edit command' : 'New command'}
                            </Typography>
                            <InputBase
                                autoFocus
                                value={label}
                                onChange={(event) => setLabel(event.target.value)}
                                placeholder="Label"
                                sx={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    marginBottom: '6px',
                                    fontFamily: 'inherit',
                                    fontSize: 12,
                                    padding: '6px 10px',
                                    borderRadius: '8px',
                                    border: `1px solid ${C.border2}`,
                                    backgroundColor: C.surface,
                                    display: 'block',
                                }}
                            />
                            <VarInput
                                multiline
                                rows={3}
                                value={prompt}
                                onChange={setPrompt}
                                placeholder="Prompt"
                                vars={vars}
                                showPreview={false}
                                ariaLabel="Prompt"
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    marginBottom: '8px',
                                    fontFamily: 'inherit',
                                    fontSize: 12,
                                    padding: '7px 10px',
                                    borderRadius: '8px',
                                    border: `1px solid ${C.border2}`,
                                    backgroundColor: C.surface,
                                    display: 'block',
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    lineHeight: 1.4,
                                    color: unavailableAttributeNames.length
                                        ? C.coral
                                        : promptAttributeNames.length
                                          ? C.teal
                                          : C.textSoft,
                                    margin: '-2px 2px 8px',
                                }}>
                                {unavailableAttributeNames.length
                                    ? `Unavailable attributes: ${unavailableAttributeNames.join(', ')}`
                                    : promptAttributeNames.length
                                      ? `${promptAttributeNames.length} selected attribute${promptAttributeNames.length === 1 ? '' : 's'} — default product context will be excluded.`
                                      : 'No attributes selected — type {{ to add one. The default product context will be used.'}
                            </Typography>
                            <Stack direction="row" gap="4px" alignItems="center">
                                {CMD_COLORS.map((color) => (
                                    <ButtonBase
                                        key={color.id}
                                        onClick={() => setColorId(color.id)}
                                        aria-label={color.id}
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: colorId === color.id ? `2px solid ${C.ink}` : '2px solid transparent',
                                            background: color.hex,
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                        }}
                                    />
                                ))}
                                <Box sx={{flex: 1}} />
                                <ButtonBase
                                    onClick={resetForm}
                                    sx={{
                                        border: `1px solid ${C.border}`,
                                        background: 'transparent',
                                        color: C.textSoft,
                                        borderRadius: '6px',
                                        padding: '4px 8px',
                                        fontFamily: 'inherit',
                                        fontSize: 11,
                                        cursor: 'pointer',
                                    }}>
                                    Cancel
                                </ButtonBase>
                                <ButtonBase
                                    onClick={() => {
                                        if (!label.trim() || !prompt.trim()) return;
                                        setCommands((current) =>
                                            editingId
                                                ? current.map((command) =>
                                                      command.id === editingId
                                                          ? {
                                                                ...command,
                                                                label: label.trim(),
                                                                prompt: prompt.trim(),
                                                                colorId,
                                                            }
                                                          : command,
                                                  )
                                                : [
                                                      ...current,
                                                      {
                                                          id: `cc${uid()}`,
                                                          label: label.trim(),
                                                          prompt: prompt.trim(),
                                                          colorId,
                                                      },
                                                  ],
                                        );
                                        resetForm();
                                    }}
                                    sx={{
                                        border: 'none',
                                        background: label.trim() && prompt.trim() ? C.ink : C.surfaceAlt,
                                        color: label.trim() && prompt.trim() ? C.surface : C.textSoft,
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        fontFamily: 'inherit',
                                        fontWeight: 700,
                                        fontSize: 11,
                                        cursor: 'pointer',
                                    }}>
                                    {editingId ? 'Save' : 'Add'}
                                </ButtonBase>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            )}
        </Box>
    );
}

/**
 * Unified group manager dialog — used by both Review (listings) and Generator (products).
 * @param {Object[]} items — array of {id, name, sku, category, group} objects
 * @param {string} groupKey — current group being managed
 * @param {Function} onSetGroup(itemId, groupValue) — set/unset group for an item
 * @param {Function} onClose
 */
export function GroupManagerDialog({items, groupKey, onClose, onSetGroup, allListings, onSetSuperSku}) {
    // Support legacy props (allListings/onSetSuperSku) for backward compat
    const normalizedItems = useMemo(() => {
        if (items) return items;
        return (allListings || []).map((l) => ({id: l.id, name: l.name, sku: l.sku, category: l.category, group: l.superSku || ''}));
    }, [items, allListings]);

    const setGroupFn = onSetGroup || onSetSuperSku || (() => {});

    const [search, setSearch] = useState('');
    const [draftGroup, setDraftGroup] = useState(groupKey || '');
    const filteredItems = useMemo(() => {
        const query = search.trim().toLowerCase();
        return normalizedItems.filter((item) => {
            if (!query) return true;
            return (
                (item.name || '').toLowerCase().includes(query) ||
                (item.sku || '').toLowerCase().includes(query) ||
                (item.category || '').toLowerCase().includes(query)
            );
        });
    }, [normalizedItems, search]);

    const members = normalizedItems.filter((item) => item.group === groupKey);

    // Match the per-item checkbox's target group so "Select all" assigns to
    // whatever name the user has typed (draftGroup) and falls back to the
    // existing groupKey otherwise — same logic as the single-row toggle.
    const targetGroup = draftGroup.trim() || groupKey;
    const allFilteredSelected =
        filteredItems.length > 0
        && filteredItems.every((item) => item.group === targetGroup);

    const handleToggleAll = () => {
        if (allFilteredSelected) {
            // Deselect every visible item that's currently in the target
            // group. Items hidden by the filter are intentionally untouched.
            filteredItems.forEach((item) => {
                if (item.group === targetGroup) {
                    setGroupFn(item.id, '');
                }
            });
        } else {
            // Add every visible item that isn't already in the target group.
            filteredItems.forEach((item) => {
                if (item.group !== targetGroup) {
                    setGroupFn(item.id, targetGroup);
                }
            });
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{sx: {...dialogPaperSx, maxWidth: 540}}}>
            <Box sx={{padding: '16px', borderBottom: `1px solid ${C.border}`}}>
                <Stack direction="row" alignItems="center" gap="8px" sx={{marginBottom: '10px'}}>
                    <SectionLabel sx={{color: C.amber, flex: 1}}>Group manager</SectionLabel>
                    <ButtonBase
                        onClick={onClose}
                        sx={{border: 'none', background: 'transparent', color: C.textSoft, cursor: 'pointer', fontSize: 18, lineHeight: 1}}>
                        ×
                    </ButtonBase>
                </Stack>
                <InputBase
                    value={draftGroup}
                    onChange={(event) => setDraftGroup(event.target.value)}
                    placeholder="Group name"
                    sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: 'monospace',
                        fontSize: 13,
                        padding: '8px 10px',
                        borderRadius: '10px',
                        border: `1px solid ${C.border2}`,
                        marginBottom: '8px',
                        display: 'block',
                    }}
                />
                <InputBase
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Filter items"
                    sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        padding: '8px 10px',
                        borderRadius: '10px',
                        border: `1px solid ${C.border2}`,
                        display: 'block',
                    }}
                />
            </Box>
            <DialogContent sx={{padding: '16px', display: 'grid', gap: '8px'}}>
                {filteredItems.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{paddingBottom: '2px'}}>
                        <Typography sx={{fontSize: 11, color: C.textFaint}}>
                            {search.trim()
                                ? `${filteredItems.length} match${filteredItems.length === 1 ? '' : 'es'}`
                                : `${filteredItems.length} item${filteredItems.length === 1 ? '' : 's'}`}
                        </Typography>
                        <ButtonBase
                            onClick={handleToggleAll}
                            sx={{
                                border: `1px solid ${C.border}`,
                                background: 'transparent',
                                color: C.ink,
                                borderRadius: '8px',
                                padding: '4px 10px',
                                fontFamily: 'inherit',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                                '&:hover': {background: C.surfaceAlt},
                            }}>
                            {allFilteredSelected
                                ? (search.trim() ? 'Deselect filtered' : 'Deselect all')
                                : (search.trim() ? 'Select filtered' : 'Select all')}
                        </ButtonBase>
                    </Stack>
                )}
                {filteredItems.map((item) => {
                    const checked = item.group === groupKey;
                    return (
                        <Box
                            key={item.id}
                            component="label"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                border: `1px solid ${checked ? `${C.amber}44` : C.border}`,
                                background: checked ? C.amberL : C.surface,
                                borderRadius: '10px',
                                padding: '9px 10px',
                                cursor: 'pointer',
                            }}>
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setGroupFn(item.id, checked ? '' : draftGroup.trim() || groupKey)}
                            />
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography sx={{fontSize: 13, fontWeight: 600, color: C.ink}}>{item.name}</Typography>
                                <Typography sx={{fontSize: 11, color: C.textSoft}}>{item.sku || 'No SKU'}</Typography>
                            </Box>
                        </Box>
                    );
                })}
            </DialogContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{padding: '16px', borderTop: `1px solid ${C.border}`}}>
                <Typography component="span" sx={{fontSize: 11, color: C.textSoft}}>{members.length} item(s) in group</Typography>
                <ButtonBase
                    onClick={onClose}
                    sx={{
                        border: 'none',
                        background: C.ink,
                        color: C.surface,
                        borderRadius: '10px',
                        padding: '8px 12px',
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}>
                    Done
                </ButtonBase>
            </Stack>
        </Dialog>
    );
}

export function GroupPicker({allListings, listing, onSetSuperSku}) {
    // Snapshot the group being managed when the dialog opens. If we read it
    // live from `listing.superSku` on every render, deselecting the current
    // listing inside the dialog flips the snapshot to "" and breaks the
    // checkbox/member filter for every other item in the group.
    const [managedGroupKey, setManagedGroupKey] = useState(null);
    const showManager = managedGroupKey !== null;
    const [showCreate, setShowCreate] = useState(false);
    const [newGroup, setNewGroup] = useState('');
    const groups = Array.from(new Set(allListings.map((item) => item.superSku).filter(Boolean))).filter(
        (group) => group !== listing.superSku,
    );

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
            {listing.superSku ? (
                <>
                    <ButtonBase
                        onClick={() => setManagedGroupKey(listing.superSku)}
                        sx={{
                            border: `1px solid ${C.amber}44`,
                            background: C.amberL,
                            color: C.amber,
                            borderRadius: 999,
                            padding: '4px 10px',
                            fontFamily: 'inherit',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}>
                        Group: {listing.superSku}
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => onSetSuperSku(listing.id, '')}
                        sx={{
                            border: `1px solid ${C.border}`,
                            background: 'transparent',
                            color: C.textSoft,
                            borderRadius: 999,
                            padding: '4px 10px',
                            fontFamily: 'inherit',
                            fontSize: 11,
                            cursor: 'pointer',
                        }}>
                        Remove
                    </ButtonBase>
                </>
            ) : (
                <>
                    {groups.length > 0 && (
                        <Box
                            component="select"
                            aria-label="Join existing group"
                            defaultValue=""
                            onChange={(event) => event.target.value && onSetSuperSku(listing.id, event.target.value)}
                            sx={{
                                fontFamily: 'inherit',
                                fontSize: 11,
                                padding: '5px 8px',
                                borderRadius: 999,
                                border: `1px solid ${C.border2}`,
                                background: C.surface,
                            }}>
                            <option value="">Join existing group</option>
                            {groups.map((group) => (
                                <option key={group} value={group}>
                                    {group}
                                </option>
                            ))}
                        </Box>
                    )}
                    <ButtonBase
                        onClick={() => setShowCreate((current) => !current)}
                        sx={{
                            border: `1px solid ${C.border}`,
                            background: 'transparent',
                            color: C.textSoft,
                            borderRadius: 999,
                            padding: '4px 10px',
                            fontFamily: 'inherit',
                            fontSize: 11,
                            cursor: 'pointer',
                        }}>
                        New group
                    </ButtonBase>
                    {showCreate && (
                        <Stack direction="row" gap="6px" alignItems="center">
                            <InputBase
                                value={newGroup}
                                onChange={(event) => setNewGroup(event.target.value)}
                                placeholder="e.g. OKN-ARC"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: 11,
                                    padding: '5px 8px',
                                    borderRadius: 999,
                                    border: `1px solid ${C.border2}`,
                                    background: C.surface,
                                }}
                            />
                            <ButtonBase
                                onClick={() => {
                                    if (!newGroup.trim()) return;
                                    onSetSuperSku(listing.id, newGroup.trim());
                                    setShowCreate(false);
                                    setNewGroup('');
                                }}
                                sx={{
                                    border: 'none',
                                    background: C.amber,
                                    color: C.surface,
                                    borderRadius: 999,
                                    padding: '5px 10px',
                                    fontFamily: 'inherit',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}>
                                Create
                            </ButtonBase>
                        </Stack>
                    )}
                </>
            )}

            {showManager && (
                <GroupManagerDialog
                    allListings={allListings}
                    groupKey={managedGroupKey}
                    onClose={() => setManagedGroupKey(null)}
                    onSetSuperSku={onSetSuperSku}
                />
            )}
        </Box>
    );
}

export function ApplyToGroupDialog({
    apiKey,
    charLimits = {},
    onApply,
    onClose,
    open,
    profileSettings = null,
    siblings,
    sourceListing,
}) {
    const {adaptVariantText} = useReviewAI({profileSettings});
    const [targets, setTargets] = useState(() => Object.fromEntries(siblings.map((listing) => [listing.id, true])));
    const [fieldModes, setFieldModes] = useState(() =>
        Object.fromEntries(
            sourceListing.fields
                .filter((field) => field.text.trim())
                .map((field) => [field.id, field.type === 'bullet' ? 'copy' : 'adapt']),
        ),
    );
    const [running, setRunning] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setTargets(Object.fromEntries(siblings.map((listing) => [listing.id, true])));
    }, [siblings]);

    if (!open) return null;

    const sourceFields = sourceListing.fields.filter((field) => field.text.trim());
    const activeTargets = siblings.filter((listing) => targets[listing.id]);

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{sx: dialogPaperSx}}>
            <Box sx={{padding: '16px', borderBottom: `1px solid ${C.border}`}}>
                <Stack direction="row" alignItems="center" gap="8px">
                    <SectionLabel sx={{color: C.amber, flex: 1}}>Apply to group</SectionLabel>
                    <ButtonBase
                        onClick={onClose}
                        sx={{border: 'none', background: 'transparent', color: C.textSoft, cursor: 'pointer', fontSize: 18, lineHeight: 1}}>
                        ×
                    </ButtonBase>
                </Stack>
                <Typography sx={{fontSize: 12, color: C.textSoft, marginTop: '6px'}}>
                    Copy or adapt content from {sourceListing.name} to sibling variants.
                </Typography>
            </Box>
            <DialogContent sx={{padding: '16px', display: 'grid', gap: '16px'}}>
                <Box>
                    <SectionLabel sx={{marginBottom: '8px'}}>Target variants</SectionLabel>
                    <Box sx={{display: 'grid', gap: '8px'}}>
                        {siblings.map((listing) => (
                            <Box
                                key={listing.id}
                                component="label"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    border: `1px solid ${targets[listing.id] ? `${C.amber}44` : C.border}`,
                                    background: targets[listing.id] ? C.amberL : C.surface,
                                    borderRadius: '10px',
                                    padding: '9px 10px',
                                    cursor: 'pointer',
                                }}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(targets[listing.id])}
                                    onChange={() =>
                                        setTargets((current) => ({
                                            ...current,
                                            [listing.id]: !current[listing.id],
                                        }))
                                    }
                                />
                                <Box sx={{flex: 1}}>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, color: C.ink}}>{listing.name}</Typography>
                                    <Typography sx={{fontSize: 11, color: C.textSoft}}>{listing.sku || 'No SKU'}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box>
                    <SectionLabel sx={{marginBottom: '8px'}}>Field modes</SectionLabel>
                    <Box sx={{display: 'grid', gap: '8px'}}>
                        {sourceFields.map((field) => (
                            <Box
                                key={field.id}
                                sx={{
                                    border: `1px solid ${C.border}`,
                                    borderRadius: '10px',
                                    padding: '10px 12px',
                                    background: C.surface,
                                }}>
                                <Stack direction="row" alignItems="center" gap="8px" sx={{marginBottom: '8px'}}>
                                    <Typography component="span" sx={{fontSize: 12, fontWeight: 600, color: C.ink}}>{field.label}</Typography>
                                    <Typography component="span" sx={{fontSize: 10, color: C.textSoft}}>{field.type}</Typography>
                                </Stack>
                                <Box sx={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                                    {['copy', 'adapt', 'skip'].map((mode) => (
                                        <ButtonBase
                                            key={mode}
                                            onClick={() =>
                                                setFieldModes((current) => ({
                                                    ...current,
                                                    [field.id]: mode,
                                                }))
                                            }
                                            sx={{
                                                border: `1px solid ${
                                                    fieldModes[field.id] === mode
                                                        ? mode === 'copy'
                                                            ? `${C.teal}44`
                                                            : mode === 'adapt'
                                                              ? `${C.blue}44`
                                                              : `${C.border2}`
                                                        : C.border
                                                }`,
                                                background:
                                                    fieldModes[field.id] === mode
                                                        ? mode === 'copy'
                                                            ? C.tealL
                                                            : mode === 'adapt'
                                                              ? C.blueL
                                                              : C.surfaceAlt
                                                        : 'transparent',
                                                color:
                                                    mode === 'copy'
                                                        ? C.teal
                                                        : mode === 'adapt'
                                                          ? C.blue
                                                          : C.textSoft,
                                                borderRadius: 999,
                                                padding: '4px 10px',
                                                fontFamily: 'inherit',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                            }}>
                                            {mode}
                                        </ButtonBase>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {error && (
                    <Box
                        sx={{
                            border: `1px solid ${C.coral}33`,
                            background: C.coralL,
                            color: C.coral,
                            borderRadius: '10px',
                            padding: '8px 10px',
                            fontSize: 11,
                        }}>
                        {error}
                    </Box>
                )}
            </DialogContent>
            <Stack direction="row" gap="8px" sx={{padding: '16px', borderTop: `1px solid ${C.border}`}}>
                <ButtonBase
                    onClick={onClose}
                    sx={{
                        border: `1px solid ${C.border}`,
                        background: 'transparent',
                        color: C.textSoft,
                        borderRadius: '10px',
                        padding: '8px 12px',
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}>
                    Cancel
                </ButtonBase>
                <ButtonBase
                    onClick={async () => {
                        setRunning(true);
                        setError('');
                        try {
                            for (const target of activeTargets) {
                                for (const field of sourceFields) {
                                    const mode = fieldModes[field.id];
                                    if (mode === 'skip') continue;
                                    const nextText =
                                        mode === 'adapt'
                                            ? await adaptVariantText({
                                                  apiKey,
                                                  charLimit: charLimits[field.type] || 0,
                                                  sourceListing,
                                                  targetListing: target,
                                                  field,
                                              })
                                            : field.text;
                                    onApply(target.id, field.label, field.type, nextText);
                                }
                            }
                            onClose();
                        } catch (applyError) {
                            setError(applyError.message || 'Could not apply content to the group.');
                        } finally {
                            setRunning(false);
                        }
                    }}
                    disabled={running || !activeTargets.length}
                    sx={{
                        border: 'none',
                        background: C.ink,
                        color: C.surface,
                        borderRadius: '10px',
                        padding: '8px 12px',
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minWidth: 160,
                    }}>
                    {running ? (
                        <Stack component="span" direction="row" alignItems="center" gap="8px">
                            <Spinner />
                            Applying
                        </Stack>
                    ) : (
                        `Apply to ${activeTargets.length} variant(s)`
                    )}
                </ButtonBase>
            </Stack>
        </Dialog>
    );
}
